import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { EASE } from "@/lib/motion";

const H1_LINES = [
  "We turn FREE healthcare",
  "into a customer",
  { text: "acquisition & retention", italic: true, accent: true },
  "tool.",
];

export default function KineticHero() {
  const reduced = useReducedMotion();
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches;
  const fastEntrance = reduced || isMobile;

  return (
    <section
      data-testid="kinetic-hero"
      className="relative bg-[#0a1930] text-[#f4f0e6] overflow-hidden grain min-h-[100svh] flex flex-col"
    >
      {/* Aurora / editorial background */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
      >
        <div className="hidden md:block absolute -top-32 -right-40 w-[720px] h-[720px] rounded-full bg-[#f26522] opacity-[0.14] blur-[130px]" />
        <div className="hidden md:block absolute bottom-0 -left-40 w-[560px] h-[560px] rounded-full bg-[#f4f0e6] opacity-[0.06] blur-[120px]" />
      </motion.div>

      {/* Vertical rules */}
      <div className="absolute inset-y-0 left-6 lg:left-10 w-px bg-white/8" aria-hidden />
      <div className="absolute inset-y-0 right-6 lg:right-10 w-px bg-white/8" aria-hidden />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 pt-40 lg:pt-48 pb-16 lg:pb-24 flex-1 flex flex-col">
        {/* Chapter marker */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: fastEntrance ? 0.05 : 0.2, duration: fastEntrance ? 0.45 : 0.9 }}
          className="flex items-center gap-4 mb-14 lg:mb-20"
        >
          <span className="w-16 h-px bg-[#f4f0e6]/40" />
          <span className="overline-tag text-[#f4f0e6]/80">
            Chapter 00 — Enterprise Healthcare Partnership
          </span>
        </motion.div>

        {/* Kinetic headline */}
        <h1
          data-testid="hero-headline"
          className="font-display text-[clamp(2.75rem,7.6vw,7.75rem)] leading-[0.94] tracking-[-0.028em] font-light"
        >
          {H1_LINES.map((line, i) => {
            const text = typeof line === "string" ? line : line.text;
            const italic = typeof line === "object" && line.italic;
            const accent = typeof line === "object" && line.accent;
            return (
              <span className="mask-line" key={i}>
                <motion.span
                  className={`inline-block ${italic ? "font-display-italic" : ""} ${
                    accent ? "text-[#f26522]" : ""
                  }`}
                  initial={reduced ? { y: 0 } : { y: fastEntrance ? "45%" : "115%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: fastEntrance ? 0.55 : 1.05,
                    ease: EASE,
                    delay: fastEntrance ? 0.1 + i * 0.06 : 0.45 + i * 0.14,
                  }}
                >
                  {text}
                </motion.span>
              </span>
            );
          })}
        </h1>

        {/* Sub + CTAs */}
        <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: fastEntrance ? 0.35 : 1.4, duration: fastEntrance ? 0.5 : 0.9, ease: EASE }}
            className="lg:col-span-6 text-lg lg:text-xl text-[#f4f0e6]/75 leading-relaxed max-w-[520px]"
            data-testid="hero-subline"
          >
            Medicual brings a{" "}
            <span className="text-[#f4f0e6] font-display italic">
              licensed clinical pathway
            </span>{" "}
            into your offer — giving customers a clear digital entry point before
            care begins.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: fastEntrance ? 0.45 : 1.55, duration: fastEntrance ? 0.5 : 0.9, ease: EASE }}
            className="lg:col-span-6 flex flex-col sm:flex-row lg:justify-end items-start sm:items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-3 bg-[#f26522] text-[#0a1930] px-7 py-4 overline-tag hover:bg-[#f4f0e6] transition-colors"
            >
              Contact us
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/how-it-works"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center gap-3 text-[#f4f0e6] overline-tag hover:text-[#f26522] transition-colors"
            >
              <span className="w-8 h-px bg-current" />
              See how it works
            </Link>
          </motion.div>
        </div>

        {/* Signature footer strip inside hero */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: fastEntrance ? 0.6 : 1.9, duration: fastEntrance ? 0.5 : 1 }}
          className="mt-16 lg:mt-24 hidden lg:grid grid-cols-4 gap-6 border-t border-white/12 pt-6"
        >
          {[
            { k: "01", v: "CE-marked" },
            { k: "02", v: "Since 2013" },
            { k: "03", v: "90%+ resolved online" },
            { k: "04", v: "15 min average handling time" },
          ].map((f) => (
            <div key={f.k} className="flex items-baseline gap-3">
              <span className="overline-tag text-[#f26522]">{f.k}</span>
              <span className="text-sm text-[#f4f0e6]/80">{f.v}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Parallax mark */}
      <motion.div
        aria-hidden
        className="absolute right-6 lg:right-16 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none select-none"
      >
        <span className="font-display text-[26vw] leading-none">+</span>
      </motion.div>
    </section>
  );
}
